import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { ChevronDown, FileX, Download, Info, Plus, Search } from 'lucide-react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import {
  PublicGrade,
  PublicSubject,
  publicGetSchoolGrades,
  publicGetSchoolGradeSubjects,
  editExamInfo,
  adminTransferExam,
  adminUpdateExam,
  adminListInstitutionsBasic,
  adminGetExams,
  adminGetNationalCoverage,
  AdminExamListItem,
} from '../../utils/remote';
import type { AdminCoverageLevel, AdminCoverageStatus } from '../../types/admin';
import { apiErrorText } from '../../school_panel/lib/apiError';
import { fieldClass } from '../../school_panel/lib/fieldClass';
import { Badge, Button, Callout, Chip, ConfirmDialog, EmptyState, ErrorPanel, FormField, KpiCard, LoadingState, Pager, PageHeader, PanelCard, PillTabs, Segmented, Table, Tabs, Td, Th, Track } from '../../school_panel/components/ui';
import { downloadCsv, toCsv } from '../../school_panel/lib/csv';
import { AppLink, LINK_ACCENT, LINK_SMALL, gradeShort } from './adminUi';
import { useOtherSchoolGuard, type GuardedExam } from '../../components/Exams/useOtherSchoolGuard';

const PAGE_SIZE = 25;

type ScopeTab = 'all' | 'national' | 'school';
type StatusKey = 'all' | 'Active' | 'Draft';
type SortKey = 'name' | '-created' | 'created';

const SORT_LABEL: Record<SortKey, string> = { name: 'name A→Z', '-created': 'newest first', created: 'oldest first' };

const fmtCreated = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const SCOPE_PARAM: Record<ScopeTab, 'national' | 'none' | undefined> = { all: undefined, national: 'national', school: 'none' };

const INPUT = fieldClass;

type YearFilter = number | 'none' | '';
type CellFilter = { subject: string; year: number; ids: number[] } | null;

const COVERAGE_PREVIEW = 12;

const COVERAGE_LEVELS: { key: AdminCoverageLevel; label: string; subtitle: string; empty: string }[] = [
  { key: 'school', label: 'Grade 12 (ESSLCE)', subtitle: 'Grade 12 ESSLCE papers by year (E.C.) and subject', empty: 'No grade 12 national exams have a year yet.' },
  { key: 'university', label: 'University (Exit, COC, GAT)', subtitle: 'University exit, COC and GAT papers by year and department', empty: 'No university national exams have a year yet.' },
];

// Heatmap cells are blank blocks (design `.cell`): published slate, draft accent, missing a dashed ground box.
const CELL_STYLE: Record<AdminCoverageStatus, { cls: string; swatch: string; text: string }> = {
  published: { cls: 'border-transparent bg-slate', swatch: 'bg-slate', text: 'Published' },
  draft: { cls: 'border-transparent bg-accent', swatch: 'bg-accent', text: 'Draft' },
  missing: { cls: 'border border-dashed border-cell-dash bg-ground', swatch: 'border border-dashed border-cell-dash bg-ground', text: 'Missing' },
};

const errText = (e: unknown) => apiErrorText(e, 'Unknown error');

function scopePill(scope?: string) {
  if (scope === 'national') return <Badge tone="navy">National</Badge>;
  if (scope === 'international') return <Badge tone="info">International</Badge>;
  return <Badge tone="neutral">School</Badge>;
}

function statusPill(status?: string) {
  if (status === 'Active') return <Badge tone="success">Published</Badge>;
  if (status === 'Draft') return <Badge tone="warning">Draft</Badge>;
  return <Badge tone="neutral">{status || '—'}</Badge>;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const num = (n: number | undefined) => (n === undefined ? '—' : n.toLocaleString('en-US'));

function useCount(key: string, params: { scope?: 'national'; status?: 'Active' | 'Draft' }, enabled: boolean) {
  return useQuery({
    queryKey: ['admin-exams-count', key],
    queryFn: async () => (await adminGetExams({ ...params, page: 1, page_size: 1 })).count,
    enabled,
    staleTime: 30_000,
  });
}

type SchoolOption = { id: number; name: string };

function ExamDetails({ exam, token, schools, onChanged, guard }: {
  exam: AdminExamListItem;
  token: string | null;
  schools: SchoolOption[];
  onChanged: () => void;
  /** Asks once before the first change to another school's exam. */
  guard: (exam: GuardedExam, run: () => void) => void;
}) {
  const [name, setName] = useState(exam.name || '');
  const [time, setTime] = useState(exam.time_given != null ? String(exam.time_given) : '');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [year, setYear] = useState(exam.exam_year != null ? String(exam.exam_year) : '');
  const [yearBusy, setYearBusy] = useState(false);
  const [yearMsg, setYearMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [confirmStatus, setConfirmStatus] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [target, setTarget] = useState<number | ''>('');
  const [repChapters, setRepChapters] = useState(true);
  const [repQuestions, setRepQuestions] = useState(true);
  const [confirmTransfer, setConfirmTransfer] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferMsg, setTransferMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const published = exam.status === 'Active';
  const nextStatus = published ? 'Draft' : 'Active';

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveMsg(null);
    try {
      // editExamInfo returns (rather than throws) the failure, so check for the exam payload.
      const res: any = await editExamInfo({ toBeValidated: token, exam_id: String(exam.id), payload: { name, time_minutes: time } });
      if (!res || res instanceof Error || !res.exam) throw res ?? new Error('Save failed');
      setSaveMsg({ ok: true, text: 'Saved.' });
      onChanged();
    } catch (err) {
      setSaveMsg({ ok: false, text: `Could not save: ${errText(err)}` });
    } finally {
      setSaving(false);
    }
  }

  const yearNum = Number(year);
  const yearValid = year.trim() === '' || (Number.isInteger(yearNum) && yearNum >= 1900 && yearNum <= 2100);

  async function onSaveYear(e: React.FormEvent) {
    e.preventDefault();
    if (!yearValid) return;
    setYearBusy(true);
    setYearMsg(null);
    try {
      await adminUpdateExam(exam.id, { exam_year: year.trim() === '' ? null : yearNum });
      setYearMsg({ ok: true, text: 'Year saved.' });
      onChanged();
    } catch (err) {
      setYearMsg({ ok: false, text: `Could not save the year: ${errText(err)}` });
    } finally {
      setYearBusy(false);
    }
  }

  async function onConfirmStatus() {
    setStatusBusy(true);
    setStatusError(null);
    try {
      await adminUpdateExam(exam.id, { status: nextStatus });
      setConfirmStatus(false);
      onChanged();
    } catch (err) {
      setConfirmStatus(false);
      setStatusError(`Could not ${published ? 'unpublish' : 'publish'}: ${errText(err)}`);
    } finally {
      setStatusBusy(false);
    }
  }

  async function onTransfer() {
    if (!target) return;
    // The confirm dialog stays open (busy) until the request settles.
    setTransferring(true);
    setTransferMsg(null);
    try {
      const res = await adminTransferExam(exam.id, {
        target_institution_id: target,
        replicate_chapters: repChapters,
        replicate_questions: repQuestions,
      });
      setTransferMsg({ ok: true, text: res?.detail || 'Exam transferred.' });
      onChanged();
    } catch (err) {
      setTransferMsg({ ok: false, text: `Transfer failed: ${errText(err)}` });
    } finally {
      setTransferring(false);
      setConfirmTransfer(false);
    }
  }

  const targetName = schools.find((s) => s.id === target)?.name ?? '';
  const timeValid = Number(time) > 0;
  const targets = schools.filter((s) => s.id !== exam.institution_id);

  return (
    <div className="grid grid-cols-1 gap-5 px-1 pb-2.5 pt-1.5 min-[821px]:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
      <div className="flex flex-col gap-3.5">
        <form onSubmit={(e) => { e.preventDefault(); guard(exam, () => { void onSave(e); }); }} className="flex flex-col gap-3.5">
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <FormField label="Exam name" htmlFor={`ename-${exam.id}`}>
              <input id={`ename-${exam.id}`} className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
            </FormField>
            <FormField label="Time (minutes)" htmlFor={`etime-${exam.id}`}>
              <input id={`etime-${exam.id}`} type="number" className={INPUT} value={time} onChange={(e) => setTime(e.target.value)} />
            </FormField>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Button type="submit" variant="secondary" size="sm" loading={saving} disabled={!timeValid}>Save name and time</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => guard(exam, () => setConfirmStatus(true))}>{published ? 'Unpublish' : 'Publish'}</Button>
            <AppLink to={`/exams/exam-detail/${exam.id}`} className={LINK_SMALL}>Open exam</AppLink>
            <AppLink to={`/exams/report/${exam.id}`} className={LINK_SMALL}>Open report</AppLink>
          </div>
          {!timeValid && <p className="text-sm text-danger-ink">Enter a time greater than 0 minutes.</p>}
          {saveMsg && <p role={saveMsg.ok ? 'status' : 'alert'} className={`text-sm ${saveMsg.ok ? 'text-success' : 'text-danger-ink'}`}>{saveMsg.text}</p>}
          {statusError && <p role="alert" className="text-sm text-danger-ink">{statusError}</p>}
        </form>
        {/* Not in the artboard: the exam year feeds the national coverage grid, so it stays. */}
        <form onSubmit={(e) => { e.preventDefault(); guard(exam, () => { void onSaveYear(e); }); }} className="space-y-2">
          <div className="grid grid-cols-[140px_auto] items-end gap-3.5">
            <FormField label="Exam year" htmlFor={`eyear-${exam.id}`} hint="1900 to 2100. Blank clears it.">
              <input id={`eyear-${exam.id}`} type="number" className={INPUT} value={year} onChange={(e) => setYear(e.target.value)} />
            </FormField>
            <Button type="submit" variant="ghost" size="sm" loading={yearBusy} disabled={!yearValid}>Save year</Button>
          </div>
          {!yearValid && <p className="text-sm text-danger-ink">Enter a year between 1900 and 2100.</p>}
          {yearMsg && <p role={yearMsg.ok ? 'status' : 'alert'} className={`text-sm ${yearMsg.ok ? 'text-success' : 'text-danger-ink'}`}>{yearMsg.text}</p>}
        </form>
      </div>

      <div className="flex flex-col gap-3">
        <Callout icon={<Info size={16} className="text-accent-ink" />}>
          Owner, subject and scope are locked. Moving an exam to another institute can break its chapter links, so use Transfer.
        </Callout>
        <div className="flex flex-wrap items-center gap-2.5">
          <label htmlFor={`etarget-${exam.id}`} className="sr-only">Target school</label>
          <select id={`etarget-${exam.id}`} className={`${INPUT} !min-h-[40px] flex-[1_1_200px]`} value={target} onChange={(e) => setTarget(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Transfer to institute…</option>
            {targets.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Button type="button" variant="ghost" size="sm" disabled={!target} loading={transferring} onClick={() => setConfirmTransfer(true)}>Transfer</Button>
        </div>
        <div className="flex flex-wrap gap-4 text-[13px] text-ink">
          <label className="flex items-center gap-2">
            <input type="checkbox" className="h-[18px] w-[18px] accent-navy" checked={repChapters} onChange={(e) => setRepChapters(e.target.checked)} />
            Replicate chapters
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" className="h-[18px] w-[18px] accent-navy" checked={repQuestions} onChange={(e) => setRepQuestions(e.target.checked)} />
            Replicate questions
          </label>
        </div>
        {transferMsg && <p role={transferMsg.ok ? 'status' : 'alert'} className={`text-sm ${transferMsg.ok ? 'text-success' : 'text-danger-ink'}`}>{transferMsg.text}</p>}
      </div>

      <ConfirmDialog
        isOpen={confirmTransfer}
        title={`Copy “${exam.name}” to ${targetName}?`}
        body={`A Draft copy is created there${repChapters ? ', with chapters' : ''}${repQuestions ? ', with questions' : ''}.`}
        confirmLabel="Transfer"
        danger={false}
        busy={transferring}
        onConfirm={onTransfer}
        onCancel={() => setConfirmTransfer(false)}
      />
      <ConfirmDialog
        isOpen={confirmStatus}
        title={`${published ? 'Unpublish' : 'Publish'} “${exam.name}”?`}
        body={published ? 'Students will no longer see this exam until it is published again.' : 'Students in the exam’s audience will be able to see and take it.'}
        confirmLabel={published ? 'Unpublish' : 'Publish'}
        danger={published}
        busy={statusBusy}
        onConfirm={onConfirmStatus}
        onCancel={() => setConfirmStatus(false)}
      />
    </div>
  );
}

const AdminExams: React.FC = () => {
  const { token, role } = useAuth();
  const superOnly = role === 'superuser';
  const qc = useQueryClient();

  const [school, setSchool] = useState<number | ''>('');
  const [grade, setGrade] = useState<number | ''>('');
  const [subject, setSubject] = useState<number | ''>('');
  const [status, setStatus] = useState<StatusKey>('all');
  const [sort, setSort] = useState<SortKey>('name');
  const [scope, setScope] = useState<ScopeTab>('all');
  const [year, setYear] = useState<YearFilter>('');
  const [cell, setCell] = useState<CellFilter>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);
  const { guard, dialog: otherSchoolDialog } = useOtherSchoolGuard();
  const debouncedSearch = useDebounced(search, 300);

  const schoolsQ = useQuery({ queryKey: ['admin-institutions-basic'], queryFn: adminListInstitutionsBasic, enabled: superOnly, staleTime: 60_000 });
  const schools: SchoolOption[] = schoolsQ.data ?? [];

  const gradesQ = useQuery<PublicGrade[]>({
    queryKey: ['admin-exams-grades', school],
    queryFn: () => publicGetSchoolGrades(school as number),
    enabled: superOnly && !!school,
    staleTime: 60_000,
  });
  const subjectsQ = useQuery<PublicSubject[]>({
    queryKey: ['admin-exams-subjects', school, grade],
    queryFn: () => publicGetSchoolGradeSubjects(school as number, grade as number),
    enabled: superOnly && !!school && !!grade,
    staleTime: 60_000,
  });
  const grades = school ? gradesQ.data ?? [] : [];
  const subjects = school && grade ? subjectsQ.data ?? [] : [];

  // Back to page 1 whenever the view changes.
  useEffect(() => { setPage(1); setOpenId(null); }, [school, grade, subject, status, sort, scope, year, cell, debouncedSearch]);

  const listParams = {
    institution: school || null,
    grade: grade || null,
    subject: subject || null,
    search: debouncedSearch || undefined,
    status: status === 'all' ? undefined : status,
    scope: SCOPE_PARAM[scope],
    year,
    sort,
    page,
    // A coverage cell fetches exactly its exams by id, all on one page.
    ...(cell ? { ids: cell.ids, page_size: Math.max(1, cell.ids.length) } : { page_size: PAGE_SIZE }),
  };
  // A missing cell has no exams: list nothing instead of fetching.
  const emptyCell = !!cell && cell.ids.length === 0;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-exams', listParams],
    queryFn: () => adminGetExams(listParams),
    enabled: superOnly && !emptyCell,
    placeholderData: keepPreviousData,
  });

  const [coverageLevel, setCoverageLevel] = useState<AdminCoverageLevel>('school');
  const levelInfo = COVERAGE_LEVELS.find((l) => l.key === coverageLevel) ?? COVERAGE_LEVELS[0];
  const coverageQ = useQuery({ queryKey: ['admin-exams-coverage', coverageLevel], queryFn: () => adminGetNationalCoverage(coverageLevel), enabled: superOnly, staleTime: 30_000 });
  const coverage = coverageQ.data;
  const yearOptions = coverage?.years ?? [];
  const [showAllSubjects, setShowAllSubjects] = useState(false);
  // Most published cells first, then A→Z; the grid shows only the first COVERAGE_PREVIEW unless expanded.
  const rankedSubjects = useMemo(() => {
    const published = (s: { cells: { status: AdminCoverageStatus }[] }) => s.cells.filter((c) => c.status === 'published').length;
    return [...(coverage?.subjects ?? [])].sort((a, b) => published(b) - published(a) || a.subject.localeCompare(b.subject, undefined, { sensitivity: 'base' }));
  }, [coverage]);
  const visibleSubjects = showAllSubjects ? rankedSubjects : rankedSubjects.slice(0, COVERAGE_PREVIEW);

  const allQ = useCount('all', {}, superOnly);
  const nationalQ = useCount('national', { scope: 'national' }, superOnly);
  const publishedQ = useCount('published', { status: 'Active' }, superOnly);
  const draftQ = useCount('draft', { status: 'Draft' }, superOnly);
  const draftNationalQ = useCount('draft-national', { scope: 'national', status: 'Draft' }, superOnly);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin-exams'] });
    qc.invalidateQueries({ queryKey: ['admin-exams-count'] });
    qc.invalidateQueries({ queryKey: ['admin-exams-coverage'] });
  };

  if (!superOnly) {
    return <div className="rounded-[14px] border border-danger bg-danger-bg p-4 text-danger-ink">403 - Not authorized</div>;
  }

  const rows = emptyCell ? [] : data?.results ?? [];
  const count = emptyCell ? 0 : data?.count ?? 0;
  const pageSize = listParams.page_size;
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const publishedPct = allQ.data && publishedQ.data !== undefined ? (publishedQ.data / Math.max(1, allQ.data)) * 100 : 0;
  const draftPct = allQ.data && draftQ.data !== undefined ? (draftQ.data / Math.max(1, allQ.data)) * 100 : 0;
  const COLS = 11;
  const yearSpan = yearOptions.length > 0 ? `${Math.min(...yearOptions)}–${Math.max(...yearOptions)}` : null;
  const subtitle = allQ.data !== undefined
    ? [
      `${num(allQ.data)} exams platform-wide`,
      nationalQ.data !== undefined ? `${num(nationalQ.data)} national papers${yearSpan ? ` from ${yearSpan} E.C.` : ''}` : null,
      draftQ.data !== undefined ? `${num(draftQ.data)} drafts` : null,
    ].filter(Boolean).join(' · ') + '.'
    : undefined;
  // Exports every page of the current filters (the same list call, paged), not just the rows on screen.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const exportList = async () => {
    setExporting(true);
    setExportError(null);
    setExportNotice(null);
    try {
      const all: AdminExamListItem[] = [];
      const size = 100;
      const maxPages = 200;
      let total = 0;
      let done = false;
      for (let pg = 1; pg <= maxPages; pg += 1) {
        const res = await adminGetExams({ ...listParams, page: pg, page_size: size });
        all.push(...res.results);
        total = res.count;
        if (res.results.length < size || all.length >= res.count) { done = true; break; }
      }
      if (!done) setExportNotice(`The export was truncated at ${num(all.length)} of ${num(total)} exams (page limit reached). Narrow the filters to export the rest.`);
      downloadCsv('yada-exams.csv', toCsv(
        ['id', 'name', 'subject', 'grade', 'year', 'institute', 'scope', 'questions', 'time_min', 'status', 'created'],
        all.map((ex) => [ex.id, ex.name, ex.subject_name || ex.course_name, ex.grade_name, ex.exam_year, ex.institution_name, ex.exam_scope, ex.no_ques, ex.time_given, ex.status, ex.created]),
      ));
    } catch (err) {
      setExportError(`Could not export the list: ${errText(err)}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Platform admin · Exam bank"
        title="Exams"
        subtitle={subtitle}
        actions={(
          <>
            <Button variant="ghost" icon={<Download size={16} aria-hidden />} disabled={rows.length === 0} loading={exporting} onClick={exportList}>Export list</Button>
            <AppLink to="/exams/add-exam" className={LINK_ACCENT}><Plus size={16} aria-hidden />Add national exam</AppLink>
          </>
        )}
      />

      {exportNotice && <p role="status" className="mb-4 rounded-[12px] bg-warning-bg px-3 py-2 text-sm text-warning-ink">{exportNotice}</p>}
      {exportError && <p role="alert" className="mb-4 text-sm text-danger-ink">{exportError}</p>}

      <div className="mb-[18px] grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-12">
        <div className="grid grid-cols-2 content-start gap-[18px] min-[821px]:col-span-6 min-[1181px]:col-span-5">
          <KpiCard label="All exams" value={num(allQ.data)} sub={schools.length > 0 ? `across ${num(schools.length)} institutes` : undefined} />
          <KpiCard label="National papers" value={num(nationalQ.data)} sub="ESSLCE and university exams" />
          <KpiCard label="Published" value={num(publishedQ.data)} tone="success"
            track={<Track value={publishedPct} label="Share of exams that are published" />} />
          <KpiCard label="Drafts" value={num(draftQ.data)}
            chip={draftNationalQ.data ? <Chip tone="accent">{num(draftNationalQ.data)} national</Chip> : undefined}
            track={<Track value={draftPct} tone="accent" label="Share of exams that are drafts" />} />
        </div>

      <PanelCard
        className="min-[821px]:col-span-6 min-[1181px]:col-span-7"
        title="National exam coverage"
        subtitle={levelInfo.subtitle}
        actions={coverage && coverage.subjects.length > 0 && coverage.years.length > 0 ? (
          <span aria-label="Legend" role="group" className="flex flex-wrap gap-3 text-xs text-muted">
            {(Object.keys(CELL_STYLE) as AdminCoverageStatus[]).map((k) => (
              <span key={k} className="flex items-center gap-1.5">
                <span aria-hidden className={`inline-block h-2.5 w-2.5 rounded-[3px] ${CELL_STYLE[k].swatch}`} />
                {CELL_STYLE[k].text}
              </span>
            ))}
          </span>
        ) : undefined}
      >
        <div className="mb-3">
          <PillTabs dense label="Exam level" value={coverageLevel}
            onChange={(k) => { setCell(null); setShowAllSubjects(false); setCoverageLevel(k); }}
            options={COVERAGE_LEVELS.map((l) => ({ key: l.key, label: l.label }))} />
        </div>
        {coverageQ.isLoading ? (
          <LoadingState label="Loading coverage…" />
        ) : coverageQ.isError ? (
          <ErrorPanel title="Couldn't load coverage" body={errText(coverageQ.error)} onRetry={() => coverageQ.refetch()} />
        ) : coverage && coverage.subjects.length > 0 && coverage.years.length > 0 ? (
          <div>
            <div className="overflow-x-auto">
              <table aria-label="National exam coverage" style={{ minWidth: 560 }} className="w-full border-separate border-spacing-1 text-sm">
                <thead>
                  <tr>
                    <th scope="col" className="whitespace-nowrap pb-1 pr-2 text-left font-mono text-[11px] font-semibold text-muted">Subject</th>
                    {coverage.years.map((y) => (
                      <th key={y} scope="col" className="whitespace-nowrap pb-1 text-center font-mono text-[11px] font-semibold text-muted">{y}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleSubjects.map((s) => (
                    <tr key={s.subject}>
                      <th scope="row" className="whitespace-nowrap pr-2 text-left text-[13px] font-medium text-ink">{s.subject}</th>
                      {coverage.years.map((y, i) => {
                        const c = s.cells[i] ?? { status: 'missing' as AdminCoverageStatus, exam_ids: [] };
                        const st = CELL_STYLE[c.status];
                        const active = cell?.subject === s.subject && cell.year === y;
                        return (
                          <td key={y} className="p-0">
                            <button
                              type="button"
                              aria-label={`${s.subject} ${y}: ${c.status}${c.status === 'missing' ? '' : `, ${c.exam_ids.length} ${c.exam_ids.length === 1 ? 'exam' : 'exams'}`}`}
                              title={`${s.subject} ${y} E.C.: ${c.status}${c.status === 'missing' ? '' : ` (${c.exam_ids.length} ${c.exam_ids.length === 1 ? 'exam' : 'exams'})`}`}
                              aria-pressed={active}
                              onClick={() => { setScope('national'); setYear(y); setCell(active ? null : { subject: s.subject, year: y, ids: c.exam_ids }); }}
                              className={`flex h-7 w-full min-w-[28px] items-center justify-center rounded-[7px] p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${st.cls} ${active ? 'ring-2 ring-navy' : ''}`}>
                              {c.exam_ids.length > 1 && <span aria-hidden className="font-mono text-[11px] font-semibold text-white">{c.exam_ids.length}</span>}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {coverage.subjects.some((s) => s.cells.some((c) => c.exam_ids.length > 1)) && (
              <p className="mt-2 text-xs text-muted">A number in a cell is how many papers that year has (e.g. part 1 and part 2, or COC and exit).</p>
            )}
            {(coverage.unassigned_count > 0 || rankedSubjects.length > COVERAGE_PREVIEW) && (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {rankedSubjects.length > COVERAGE_PREVIEW && (
                  <button type="button" aria-expanded={showAllSubjects} onClick={() => setShowAllSubjects((v) => !v)}
                    className="text-[13px] font-semibold text-primary underline">
                    {showAllSubjects ? 'Show fewer' : `Show all ${rankedSubjects.length} subjects`}
                  </button>
                )}
                {coverage.unassigned_count > 0 && (
                  <button type="button" onClick={() => { setCell(null); setScope('all'); setYear('none'); }}
                    className="ml-auto rounded-full bg-warning-bg px-3 py-1.5 font-mono text-[12px] font-semibold text-warning-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                    Needs year: {coverage.unassigned_count}
                  </button>
                )}
              </div>
            )}
            {cell && (
              <p className="mt-3 text-[13px] text-muted">
                Showing {cell.subject} {cell.year}.{' '}
                <button type="button" className="font-semibold text-primary underline" onClick={() => setCell(null)}>Clear</button>
              </p>
            )}
          </div>
        ) : (
          <p className="text-[13px] text-muted">{levelInfo.empty}</p>
        )}
      </PanelCard>
      </div>

      <PanelCard>
        <Tabs
          label="Exam scope"
          value={scope}
          onChange={(s) => { setCell(null); setScope(s); }}
          items={[
            { key: 'all', label: 'All exams', count: allQ.data },
            { key: 'national', label: 'National', count: nationalQ.data },
            { key: 'school', label: 'School exams', count: allQ.data !== undefined && nationalQ.data !== undefined ? Math.max(0, allQ.data - nationalQ.data) : undefined },
          ]}
        />

        <div className="mb-4 flex flex-col gap-2.5">
          <div className="flex flex-wrap items-center gap-3">
            <span className="w-24 text-[13px] text-muted">School / institute</span>
            <div className="min-w-0 max-w-full flex-1">
              <PillTabs scroll dense label="School / Institute" value={school === '' ? 'all' : String(school)}
                onChange={(k) => { setSchool(k === 'all' ? '' : Number(k)); setGrade(''); setSubject(''); }}
                options={[{ key: 'all', label: 'All' }, ...schools.map((s) => ({ key: String(s.id), label: s.name }))]} />
            </div>
          </div>
          {grades.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="w-24 text-[13px] text-muted">Grade</span>
              <div className="min-w-0 max-w-full flex-1">
                <PillTabs scroll dense label="Grade" value={grade === '' ? 'all' : String(grade)}
                  onChange={(k) => { setGrade(k === 'all' ? '' : Number(k)); setSubject(''); }}
                  options={[{ key: 'all', label: 'All' }, ...grades.map((g) => ({ key: String(g.id), label: g.name }))]} />
              </div>
            </div>
          )}
          {grade !== '' && subjects.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="w-24 text-[13px] text-muted">Subject</span>
              <div className="min-w-0 max-w-full flex-1">
                <PillTabs scroll dense label="Subject" value={subject === '' ? 'all' : String(subject)}
                  onChange={(k) => setSubject(k === 'all' ? '' : Number(k))}
                  options={[{ key: 'all', label: 'All' }, ...subjects.map((s) => ({ key: String(s.id), label: s.name }))]} />
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <span className="w-24 text-[13px] text-muted">Year</span>
            <div className="min-w-0 max-w-full flex-1">
              <PillTabs scroll dense label="Year" value={year === '' ? 'any' : String(year)}
                onChange={(k) => { setCell(null); setYear(k === 'any' ? '' : k === 'none' ? 'none' : Number(k)); }}
                options={[{ key: 'any', label: 'Any' }, ...yearOptions.map((y) => ({ key: String(y), label: String(y) })), { key: 'none', label: 'No year' }]} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <label className="relative block max-w-[420px] flex-[1_1_240px]">
              <span className="sr-only">Search by name</span>
              <Search size={16} aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
              <input id="search" type="search" aria-label="Search by name" className={`${INPUT} !min-h-[40px] py-0 pl-10`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Exam name…" />
            </label>
            <span className="flex-1" />
            <Segmented
              label="Status"
              value={status}
              onChange={setStatus}
              options={[{ key: 'all', label: 'All' }, { key: 'Active', label: 'Published' }, { key: 'Draft', label: 'Draft' }]}
            />
            <Segmented
              label="Sort"
              value={sort}
              onChange={setSort}
              options={[{ key: 'name', label: 'Name A→Z' }, { key: '-created', label: 'Newest' }, { key: 'created', label: 'Oldest' }]}
            />
          </div>
        </div>

        <div>
          {isLoading ? (
            <LoadingState label="Loading exams…" />
          ) : isError ? (
            <ErrorPanel title="Failed to load" body="The exams could not be loaded." onRetry={() => refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState icon={<FileX size={20} />} title="No exams found" body="Change the scope, status or filters." />
          ) : (
            <Table minWidth={1080}>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Subject</Th>
                  <Th>Grade</Th>
                  <Th>Year</Th>
                  <Th>Institute</Th>
                  <Th>Scope</Th>
                  <Th align="right">Questions</Th>
                  <Th align="right">Time (min)</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                  <Th align="right"><span className="sr-only">Details</span></Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((ex) => {
                  const open = openId === ex.id;
                  return (
                    <React.Fragment key={ex.id}>
                      <tr className={open ? '[&>td]:bg-surface-2' : ''}>
                        <Td className="font-semibold text-ink">{ex.name}</Td>
                        <Td className="text-ink">{ex.subject_name || ex.course_name || '—'}</Td>
                        <Td className="font-mono font-medium text-ink">{gradeShort(ex.grade_name)}</Td>
                        <Td className="whitespace-nowrap font-mono font-medium text-ink">{ex.exam_year != null ? `${ex.exam_year} E.C.` : '—'}</Td>
                        <Td className="text-muted">{ex.institution_name || '—'}</Td>
                        <Td>{scopePill(ex.exam_scope)}</Td>
                        <Td align="right" className="font-mono font-medium text-ink">{ex.no_ques ?? '—'}</Td>
                        <Td align="right" className="font-mono font-medium text-ink">{ex.time_given ?? '—'}</Td>
                        <Td>{statusPill(ex.status)}</Td>
                        <Td className="whitespace-nowrap font-mono font-medium text-muted">{fmtCreated(ex.created)}</Td>
                        <Td align="right">
                          <button type="button" aria-label={`Details for ${ex.name}`} aria-expanded={open} onClick={() => setOpenId(open ? null : ex.id)}
                            className="inline-grid h-[30px] w-[30px] place-items-center rounded-[8px] border-0 bg-transparent p-0 text-muted hover:bg-ground hover:text-ink focus-visible:ring-2 focus-visible:ring-accent">
                            <ChevronDown size={16} aria-hidden className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
                          </button>
                        </Td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={COLS} className="border-b-0 bg-surface-2 px-2.5 py-3">
                            <ExamDetails exam={ex} token={token} schools={schools} onChanged={refresh} guard={guard} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>

        {!isLoading && !isError && count > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-hairline pt-4">
            <span className="text-[13px] text-muted">Showing {rows.length} of {count} · sorted by {SORT_LABEL[sort]}</span>
            <Pager page={page} pageCount={totalPages} onPage={setPage} label="Exams pagination" />
          </div>
        )}
      </PanelCard>
      {otherSchoolDialog}
    </div>
  );
};

export default AdminExams;
